import type { FC } from 'react'
import { Checkbox } from '@/components/commons/checkbox'
import { ExpandImageButton } from '@/components/commons/expand-image-button'
import { ImageCard } from '@/components/commons/image-card'
import { Image01Icon, Stars01Icon } from '@/components/ui/icons'
import { Tooltip } from '@/components/ui/tooltip'
import { ImageCardFlags } from '@/features/photoset/components/image-card-flags'
import {
	SpaceImageSelectionInfo,
	SpaceImagesContainer
} from '@/features/photoset/components/space-images-atoms'
import { useRemoveRetouchingQaFlag } from '@/features/photoset/hooks/use-remove-retouching-qa-flag'
import { usePhotosetStore } from '@/features/photoset/store/store'
import {
	type Flag,
	type Image,
	type Photoset,
	type PhotosetSpace,
	StepKey
} from '@/features/photoset/types'
import { useTranslation } from '@/translations/i18n'

interface RetouchingQaSpaceImagesProps {
	images: PhotosetSpace['images']
	photosetId: Photoset['id']
	photosetSpaceId: PhotosetSpace['id']
	allFlagsKeyName?: Flag['key_name'][]
	spaceIndex: number
	onExpandImage: (spaceIndex: number, imageIndex: number) => void
	isQaView: boolean
}

export const RetouchingQaSpaceImages: FC<RetouchingQaSpaceImagesProps> = ({
	images,
	photosetId,
	photosetSpaceId,
	allFlagsKeyName,
	spaceIndex,
	onExpandImage,
	isQaView
}) => {
	const { t } = useTranslation()

	const selectedImages = usePhotosetStore((state) => state.selectedImages)
	const selectImages = usePhotosetStore((state) => state.selectImages)
	const unselectImages = usePhotosetStore((state) => state.unselectImages)

	const { sendBackImage, onRemoveFlagCb } = useRemoveRetouchingQaFlag({
		photosetId,
		photosetSpaceId
	})

	const onToggleClick = (image: Omit<Image, 'space'>) => {
		sendBackImage({
			imageId: image.id,
			needs_to_review: !image.needs_to_review
		})
	}

	return (
		<SpaceImagesContainer $gap={4}>
			{images.map((image, index) => {
				const isFlaggedImage = image.flags.some((flag) =>
					allFlagsKeyName?.includes(flag.key_name)
				)
				const isSelected = selectedImages.some(
					(selectedImageId) => selectedImageId === image.id
				)

				return (
					<ImageCard
						endSlot={
							<>
								<SpaceImageSelectionInfo className='items-start gap-4'>
									<section className='flex w-full items-center justify-between gap-2 [&>.tooltip-trigger]:shrink-0'>
										<div className='flex items-center gap-2 overflow-hidden text-ellipsis whitespace-nowrap'>
											{image.is_manually_retouched ? (
												<Image01Icon size={20} />
											) : (
												<Stars01Icon size={20} />
											)}
											<h4>{image.name}</h4>
										</div>
										<Tooltip
											content={
												<p>
													{t(
														'photoset:retouching_qa.send_back_tooltip'
													)}
												</p>
											}
											contentProps={{
												sideOffset: 8,
												align: 'center',
												side: 'top'
											}}
											maxWidth={258}
											rootProps={{
												open: isFlaggedImage
													? false
													: undefined
											}}
											triggerProps={{
												className: 'tooltip-trigger'
											}}
											variant='light'
										>
											<div
												className={`flex items-center gap-1.5 [&_input[type='checkbox']]:h-4 [&_input[type='checkbox']]:w-4 ${
													isFlaggedImage && isQaView
														? '[&>span]:text-neutral-black'
														: '[&>span]:text-neutral-300'
												}`}
											>
												<Checkbox
													checked={
														image.needs_to_review
													}
													disabled={
														!(
															isFlaggedImage &&
															isQaView
														)
													}
													onChange={() =>
														onToggleClick(image)
													}
												/>
												<span className='font-medium text-xs'>
													{t(
														'photoset:retouching_qa.send_back'
													)}
												</span>
											</div>
										</Tooltip>
									</section>
									<ImageCardFlags
										hideFlagsDescriptions
										image={image}
										isReadOnly={isQaView === false}
										onRemoveFlagCb={onRemoveFlagCb}
										photosetSpaceIds={[photosetSpaceId]}
										stepId={StepKey['retouching-qa']}
										types={['visual_retouching']}
									/>
								</SpaceImageSelectionInfo>
								<ExpandImageButton
									onClick={() =>
										onExpandImage(spaceIndex, index)
									}
								/>
							</>
						}
						hideImageInfo
						image={image}
						imageNameIcon={
							image.is_manually_retouched ? (
								<Image01Icon size={20} />
							) : (
								<Stars01Icon size={20} />
							)
						}
						isSelected={isSelected}
						key={image.id}
						onSelect={
							isQaView
								? () =>
										isSelected
											? unselectImages([image.id])
											: selectImages([image.id])
								: null
						}
						thumbnail={
							image.retouched_thumbnails?.xs ??
							image.ai_retouched_thumbnails?.xs
						}
					/>
				)
			})}
		</SpaceImagesContainer>
	)
}
